import React, { useEffect, useRef } from 'react';
import { identidadeDasCenas, tocarMarco, tocarUltrapassagem } from 'features/comercial/cenas/cenasDoRanking';

/**
 * Põe no ar as cenas do Ranking, uma por vez.
 *
 * Recebe a fila pronta — quem decide QUE cena tocar é `eventosDoRanking.js`, e quem a monta com
 * nomes e fotos é o painel. Aqui só há o palco: um <canvas> por cima da tela, a cena da vez, e o
 * aviso de que ela acabou para a próxima entrar.
 *
 * A fila é consumida pela ponta: enquanto a primeira cena está no ar, as outras esperam. Quando o
 * palco sai de cena (alguém desligou as cenas, a tela foi desmontada), a que estava tocando é
 * interrompida e a trilha cala junto — animação órfã tocando som numa tela que já mudou é o tipo de
 * defeito que só aparece na TV, sem ninguém por perto.
 */

const RECURSOS = `${process.env.PUBLIC_URL || ''}/ranking-cenas/`;

/**
 * `telaInteira` é o palco fora do modo apresentação: a cena cobre a janela toda, e não só o painel.
 * O painel de mesa é mais alto que a tela, e uma comemoração desenhada por cima dele ficaria
 * metade fora de vista conforme a rolagem.
 */
const CenasDoRanking = ({ fila = [], som = true, onEncerrar, telaInteira = false, empresa = '' }) => {
  const canvas = useRef(null);
  const daVez = fila[0] || null;
  const idDaVez = daVez?.id || null;
  // Lidos na hora de tocar: mudar o som ou a identidade do callback não reinicia a cena no ar.
  const atual = useRef({});
  atual.current = { daVez, som, onEncerrar, empresa };

  useEffect(() => {
    if (!idDaVez || !canvas.current) return undefined;

    const { daVez: cena, som: comSom, empresa: marcaDaCena } = atual.current;
    const controle = new AbortController();
    let interrompida = false;
    const tocar = cena.tipo === 'ultrapassagem' ? tocarUltrapassagem : tocarMarco;

    Promise.resolve()
      .then(() => tocar(canvas.current, {
        ...cena.opcoes,
        empresa: marcaDaCena,
        som: comSom,
        recursos: RECURSOS,
        chamada: comSom && cena.chamada ? `${RECURSOS}chamadas/${cena.chamada}.mp3` : '',
        sinal: controle.signal,
      }))
      // Cena que falha não pode travar a fila: o ranking segue, só sem a festa.
      .catch((erro) => console.error('Cena do ranking não pôde ser exibida:', erro))
      .then(() => {
        if (!interrompida) atual.current.onEncerrar?.(idDaVez);
      });

    return () => {
      interrompida = true;
      controle.abort();
    };
  }, [idDaVez, empresa]);

  return (
    // Os quatro lados por extenso, e não `inset-0`: a propriedade `inset` só existe do Chromium 87
    // em diante, e no navegador da TV o palco ficava sem tamanho nenhum.
    <div
      className={`ranking-cenas-palco pointer-events-none top-0 right-0 bottom-0 left-0 ${telaInteira ? 'fixed z-[9998]' : 'absolute z-40'}`}
      aria-hidden="true"
      style={{ '--ranking-cena-veu': `rgba(${identidadeDasCenas(empresa).rgbContorno},0.82)` }}
    >
      <canvas ref={canvas} hidden className="h-full w-full object-contain" />
    </div>
  );
};

export default CenasDoRanking;
